import React, { useCallback, useEffect, useState } from 'react';
import { APP_TIMEZONE } from '../../config';
import { useQueryParams } from '../admin/useQueryParams';
import { errorMessage } from '../admin/adminApi';
import { formatDate } from '../admin/format';
import {
  Badge, DataTable, Drawer, ListLayout, Panel, SegmentedControl, Spinner, Toolbar,
  btnPrimary, btnSecondary, inputClass, labelClass
} from '../admin/AdminUI';
import { formatAmount } from '../approvals/approvalTypes';
import { downloadFile } from '../documents/documentUtils';
import { DecisionDetail, DecisionStatus } from './DecisionsTab';

const todayInputValue = () => new Date().toLocaleDateString('en-CA', { timeZone: APP_TIMEZONE });

function PaidForm({ onSubmit }) {
  const [form, setForm] = useState({ paidAt: todayInputValue(), reference: '' });
  const [saving, setSaving] = useState(false);
  return (
    <form onSubmit={async (e) => { e.preventDefault(); setSaving(true); await onSubmit(form); setSaving(false); }} className="mt-4 pt-4 border-t border-border space-y-2">
      <p className="text-sm text-muted-foreground">Pay it from the strata's bank account first, then record it here. The app never moves money.</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label htmlFor="paid-at" className={labelClass}>Paid on</label>
          <input id="paid-at" type="date" required value={form.paidAt} onChange={(e) => setForm({ ...form, paidAt: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label htmlFor="paid-reference" className={labelClass}>Reference</label>
          <input id="paid-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className={inputClass} placeholder="e.g., online banking 4471" />
        </div>
      </div>
      <button type="submit" disabled={saving} className={btnPrimary}>Mark paid</button>
    </form>
  );
}

function InvoiceDrawer({ invoice, api, slug, canAct, onChanged, onClose }) {
  const markPaid = async (form) => {
    try {
      await api.post(`/${slug}/invoices/${invoice._id}/paid`, form);
      onChanged();
    } catch (err) {
      alert(errorMessage(err, 'Failed to mark the invoice paid'));
    }
  };
  return (
    <Drawer
      title={`${invoice.vendorName} · ${formatAmount(invoice.total)}`}
      badges={<><Badge className={invoice.status === 'paid' ? 'text-success border-success normal-case' : 'text-primary border-primary normal-case'}>{invoice.status === 'paid' ? 'Paid' : 'To pay'}</Badge>{invoice.test && <Badge className="text-warning border-warning">Test</Badge>}</>}
      subtitle={`${invoice.invoiceNumber ? `Invoice ${invoice.invoiceNumber} · ` : ''}due ${formatDate(invoice.dueDate)}`}
      onClose={onClose}
    >
      <p className="text-sm text-muted-foreground">{formatAmount(invoice.subtotal)} + GST {formatAmount(invoice.gst)}</p>
      {invoice.description && <p className="mt-2 text-sm text-foreground">{invoice.description}</p>}
      {invoice.file && (
        <button type="button" onClick={() => downloadFile(() => api.download(`/${slug}/invoices/${invoice._id}/file`), invoice.file.name).catch(() => alert('Failed to download the invoice'))} className={`${btnSecondary} mt-3`}>
          Download {invoice.file.name}
        </button>
      )}
      {invoice.status === 'paid' && (
        <p className="mt-4 text-sm text-foreground">Paid {formatDate(invoice.paidAt)} by {invoice.paidByName}{invoice.paymentReference ? ` · ${invoice.paymentReference}` : ''}</p>
      )}
      {invoice.status === 'to_pay' && (canAct ? <PaidForm onSubmit={markPaid} /> : <p className="mt-4 text-sm text-muted-foreground">Pick who you are acting as to record the payment.</p>)}
    </Drawer>
  );
}

function NewInvoiceForm({ api, slug, onSaved, onCancel }) {
  const [form, setForm] = useState({ vendorId: '', vendorName: '', invoiceNumber: '', invoiceDate: '', dueDate: '', subtotal: '', gst: '', total: '', description: '' });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);

  // Picking a listed vendor carries its insurance checks and conflicts of interest onto the invoice.
  useEffect(() => {
    api.get(`/${slug}/vendors`).then((r) => setVendors((r.data.vendors || []).filter((v) => v.active))).catch(() => setVendors([]));
  }, [api, slug]);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = new FormData();
    body.append('file', file);
    Object.entries(form).forEach(([key, value]) => value !== '' && body.append(key, value));
    try {
      const response = await api.post(`/${slug}/invoices`, body);
      onSaved(response.data.decision);
    } catch (err) {
      alert(errorMessage(err, 'Failed to add the invoice'));
    } finally {
      setSaving(false);
    }
  };
  const field = (key, label, props = {}) => (
    <div>
      <label htmlFor={`new-invoice-${key}`} className={labelClass}>{label}</label>
      <input id={`new-invoice-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={inputClass} {...props} />
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="new-invoice-file" className={labelClass}>Invoice file *</label>
        <input id="new-invoice-file" type="file" required onChange={(e) => setFile(e.target.files[0] || null)} className="text-sm text-foreground" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="new-invoice-vendorId" className={labelClass}>Vendor *</label>
          <select id="new-invoice-vendorId" value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} className={inputClass}>
            <option value="">Not in the list</option>
            {vendors.map((v) => <option key={v._id} value={v._id}>{v.name}</option>)}
          </select>
        </div>
        {form.vendorId ? field('invoiceNumber', 'Invoice number') : field('vendorName', 'Vendor name *', { required: true })}
        {!form.vendorId && field('invoiceNumber', 'Invoice number')}
        {field('invoiceDate', 'Invoice date', { type: 'date' })}
        {field('dueDate', 'Due date', { type: 'date' })}
        {field('subtotal', 'Subtotal', { type: 'number', min: '0', step: '0.01' })}
        {field('gst', 'GST', { type: 'number', min: '0', step: '0.01' })}
        {field('total', 'Total *', { type: 'number', min: '0', step: '0.01', required: true })}
      </div>
      {field('description', 'What it is for')}
      <p className="text-xs text-muted-foreground">An invoice council enters goes straight to the council vote (Treasurer + one more).</p>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? 'Saving...' : 'Put to council'}</button>
        <button type="button" onClick={onCancel} className={btnSecondary}>Cancel</button>
      </div>
    </form>
  );
}

const VIEWS = [
  { value: 'approve', label: 'To approve' },
  { value: 'to_pay', label: 'To pay' },
  { value: 'paid', label: 'Paid' }
];

function InvoicesTab({ slug, info, api }) {
  const [params, setParams] = useQueryParams();
  const view = VIEWS.some((v) => v.value === params.get('view')) ? params.get('view') : 'approve';
  const selectedId = params.get('item');
  const creating = params.get('new') === '1';
  const [rows, setRows] = useState(null);
  const canAct = !!info.membership;

  const load = useCallback(async () => {
    try {
      const response = view === 'approve'
        ? await api.get(`/${slug}/decisions`, { status: 'waiting', type: 'invoice' })
        : await api.get(`/${slug}/invoices`, { status: view });
      setRows(view === 'approve' ? response.data.decisions : response.data.invoices);
    } catch (err) {
      alert(errorMessage(err, 'Failed to load invoices'));
    }
  }, [api, slug, view]);

  useEffect(() => { setRows(null); load(); }, [load]);

  const selected = (rows || []).find((r) => r._id === selectedId);
  const vendorOf = (r) => (view === 'approve' ? r.payload.vendorName : r.vendorName);
  const amountOf = (r) => (view === 'approve' ? r.payload.total : r.total);
  const dueOf = (r) => (view === 'approve' ? r.payload.dueDate : r.dueDate);

  const columns = [
    { key: 'vendor', header: 'Vendor', className: 'font-medium text-foreground', render: vendorOf },
    { key: 'amount', header: 'Amount', render: (r) => formatAmount(amountOf(r)) },
    { key: 'due', header: 'Due', className: 'whitespace-nowrap text-muted-foreground', render: (r) => formatDate(dueOf(r)) },
    view === 'approve'
      ? { key: 'status', header: 'Signatures', render: (r) => <DecisionStatus decision={r} /> }
      : { key: 'paid', header: view === 'paid' ? 'Paid' : 'Number', className: 'text-muted-foreground', render: (r) => (view === 'paid' ? formatDate(r.paidAt) : r.invoiceNumber || '—') }
  ];

  const close = () => setParams({ item: null });
  const drawer = creating ? (
    <Drawer title="Add an invoice" onClose={() => setParams({ new: null })}>
      {canAct ? <NewInvoiceForm api={api} slug={slug} onCancel={() => setParams({ new: null })} onSaved={(d) => { setParams({ new: null, view: null, item: d._id }); load(); }} />
        : <p className="text-sm text-muted-foreground">Pick who you are acting as first.</p>}
    </Drawer>
  ) : selected ? (view === 'approve'
    ? <DecisionDetail key={selected._id} decision={selected} api={api} slug={slug} canAct={canAct} onChanged={load} onClose={close} />
    : <InvoiceDrawer key={selected._id} invoice={selected} api={api} slug={slug} canAct={canAct} onChanged={load} onClose={close} />
  ) : null;

  return (
    <ListLayout drawer={drawer}>
      <Panel>
        <Toolbar>
          <h2 className="text-base font-semibold text-foreground mr-2">Invoices</h2>
          <SegmentedControl label="Invoices" value={view} onChange={(value) => setParams({ view: value === 'approve' ? null : value, item: null })} options={VIEWS} />
          <button type="button" onClick={() => setParams({ new: '1', item: null })} className={`${btnPrimary} ml-auto`}>+ Add invoice</button>
        </Toolbar>
        {!rows ? <Spinner /> : (
          <DataTable
            label="Invoices"
            columns={columns}
            rows={rows}
            selectedId={selectedId}
            onRowClick={(r) => setParams({ item: r._id, new: null })}
            emptyMessage={{ approve: 'No invoices waiting for council.', to_pay: 'Nothing to pay.', paid: 'No paid invoices yet.' }[view]}
          />
        )}
      </Panel>
    </ListLayout>
  );
}

export default InvoicesTab;
